import { ReactNode } from 'react';
import { IntlProvider as SharedIntlProvider } from '@anyroad/providers';
import { loadLocaleData } from 'lib/loadLocaleData';
import { getLegacyLocale } from 'lib/getUserLocale';

type Props = {
  children: ReactNode;
};

/**
 * IntlProvider wrapper for legacy frontend
 *
 * Uses the shared IntlProvider from @anyroad/providers package
 * with legacy-specific locale loading and detection logic.
 *
 * This provider enables react-intl localization for all components
 * in the legacy frontend application.
 *
 * Locale detection uses a priority-based system for backwards compatibility:
 * 1. Query parameter (?lang=XX-YY) - Used by external integrations
 * 2. SessionStore i18n value - Set by Rails backend from session[:i18n]
 * 3. Browser locale - Falls back to user's browser settings
 *
 * This ensures compatibility with:
 * - 22+ legacy components using SessionStore.state.get('i18n')
 * - SessionLanguageChooser component that sets ?lang= query params
 * - External integrations that pass locale via URL
 * - Rails backend session[:i18n] persistence
 *
 * @see legacy/frontend/components/lib/getUserLocale.ts for locale detection logic
 * @see legacy/frontend/components/services/translation/index.js for legacy translation service
 */
export const IntlProvider = ({ children }: Props) => (
  <SharedIntlProvider
    loadLocaleData={loadLocaleData}
    getUserLocale={getLegacyLocale}
  >
    {children}
  </SharedIntlProvider>
);
